import { Accordion } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  QuoteDocumentType,
  QuoteDocumentTypeRequest,
  QuoteDocumentTypeRequestSchema,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { MdClear, MdEdit } from 'react-icons/md';
import { handleApiErrorToast } from '../../../../../api/axios';
import {
  useCreateQuoteDocumentType,
  useDeleteQuoteDocumentType,
  useGetQuoteDocumentTypes,
  useUpdateQuoteDocumentType,
} from '../../../../../api/settingsService';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MStack,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { MSettingAccordionItem } from '../../../../../components/Monetize/MSettingAccordionItem';
import { useAccordionControl } from '../../../../../hooks';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../../../hooks/useSettingsSectionHighlight';
import { useConfirmModal } from '../../../../../services/confirmModal';
import { logger } from '../../../../../services/logger';
import { QUOTE_SETTING_KEYS } from '../quoteSettingsConfig';
import { QuoteDocumentTypeForm } from './QuoteDocumentTypeForm';

const DEFAULT_DOCUMENT_TYPE = {
  label: '',
  value: '',
} as const;

export const QuoteDocumentTypes = () => {
  const isDocTypesHighlighted = useSettingsSectionHighlight(
    QUOTE_SETTING_KEYS.quoteDocumentTypes,
  );
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<QuoteDocumentTypeRequest>({
    resolver: zodResolver(QuoteDocumentTypeRequestSchema),
    mode: 'onSubmit',
    defaultValues: DEFAULT_DOCUMENT_TYPE,
  });

  const { data: quoteDocumentTypes, isLoading } = useGetQuoteDocumentTypes();

  const { mutate: createDocumentType, isPending: createLoading } =
    useCreateQuoteDocumentType({
      onSuccess: () => {
        reset(DEFAULT_DOCUMENT_TYPE);
        setIsAddingNew(false);
      },
      onError: (err: any) => handleApiErrorToast(err),
    });

  const { mutate: updateDocumentType, isPending: updateLoading } =
    useUpdateQuoteDocumentType({
      onSuccess: () => {
        reset(DEFAULT_DOCUMENT_TYPE);
        setEditingId(null);
      },
      onError: (err: any) => handleApiErrorToast(err),
    });

  const { mutate: deleteDocumentType, isPending: deleteLoading } =
    useDeleteQuoteDocumentType({
      onError: (err: any) => handleApiErrorToast(err),
    });

  const onSubmit = (payload: QuoteDocumentTypeRequest) => {
    if (editingId) {
      updateDocumentType({
        id: editingId,
        data: payload,
      });
    } else {
      createDocumentType(payload);
    }
  };

  const onError = (err: any) => {
    logger.warn(err);
  };

  const handleDeleteDocumentType = (docTypeId: string, label: string) => {
    const onYes = async () => {
      try {
        setModalLoading(true);
        deleteDocumentType(docTypeId);
        closeModal();
      } catch (err) {
        handleApiErrorToast(err);
      } finally {
        setModalLoading(false);
      }
    };

    showConfirmModal({
      title: `Are you sure you want to delete "${label}"?`,
      description:
        'Once removed, the document type will no longer be available.',
      onYes,
      yesBtnProps: {
        variant: 'delete' as any,
      },
      noBtnProps: {
        variant: 'cancel' as any,
      },
    });
  };

  const handleEditClick = (docType: QuoteDocumentType) => {
    setEditingId(docType.id);
    setIsAddingNew(false);
    reset({
      label: docType.label,
    });
  };

  const isEditing = (id: string) => editingId === id;

  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();

  useEffect(() => {
    if (isDocTypesHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isDocTypesHighlighted, setAccordionValue]);

  return (
    <MStack w="100%" data-testid="quote-document-types">
      <Accordion.Root
        multiple
        w="full"
        value={accordionValue}
        onValueChange={({ value }) => handleAccordionChange(value)}
      >
        <MSettingAccordionItem
          value="0"
          id={QUOTE_SETTING_KEYS.quoteDocumentTypes}
          {...getHighlightStyles(isDocTypesHighlighted)}
          description="Manage custom document types for your quotes."
          label="Quote Document Types"
          borderWidth="1px"
        >
          {quoteDocumentTypes?.map((docType, index) =>
            isEditing(docType.id) ? (
              <MBox
                key={docType.id}
                borderBottomWidth={
                  index !== quoteDocumentTypes.length - 1 ? '1px' : 0
                }
                borderBottomColor="border.subtle"
                py={3}
              >
                <QuoteDocumentTypeForm
                  control={control}
                  errors={errors}
                  onSubmit={handleSubmit(onSubmit, onError)}
                  onCancel={() => {
                    setEditingId(null);
                    reset(DEFAULT_DOCUMENT_TYPE);
                  }}
                  isLoading={updateLoading}
                  isDirty={isDirty}
                />
              </MBox>
            ) : (
              <MFlex
                key={docType.id}
                justifyContent="space-between"
                alignItems="center"
                borderBottomWidth={
                  index !== quoteDocumentTypes.length - 1 ? '1px' : 0
                }
                borderBottomColor="border.subtle"
                py={3}
                h={57}
              >
                <MBox>
                  <MText py={0} m={0} fontSize="md">
                    {docType.label}
                  </MText>
                </MBox>
                <MStack gap={4} direction="row" alignItems="center">
                  <MTooltip label="Edit" placement="bottom-end">
                    <MBox>
                      <MCustomIconButton
                        isDisabled={editingId !== null || isAddingNew}
                        variant="icon"
                        btnSize={5}
                        padding={0}
                        containerSize={8}
                        iconProps={{
                          _active: { color: 'unset' },
                          _focus: { color: 'unset' },
                        }}
                        icon={MdEdit}
                        aria-label="Edit"
                        onClick={() => handleEditClick(docType)}
                      />
                    </MBox>
                  </MTooltip>
                  <MTooltip label="Remove" placement="bottom-end">
                    <MBox>
                      <MCustomIconButton
                        isLoading={deleteLoading}
                        isDisabled={editingId !== null || isAddingNew}
                        variant="icon"
                        btnSize={4}
                        padding={0}
                        containerSize={6}
                        iconProps={{
                          _active: { color: 'unset' },
                          _focus: { color: 'unset' },
                        }}
                        icon={MdClear}
                        aria-label="Remove"
                        onClick={() => {
                          handleDeleteDocumentType(docType.id, docType.label);
                        }}
                      />
                    </MBox>
                  </MTooltip>
                </MStack>
              </MFlex>
            ),
          )}
          <MBox mt={4} />

          {(!quoteDocumentTypes?.length || isAddingNew) && (
            <QuoteDocumentTypeForm
              control={control}
              errors={errors}
              onSubmit={handleSubmit(onSubmit, onError)}
              onCancel={() => {
                setIsAddingNew(false);
                reset(DEFAULT_DOCUMENT_TYPE);
              }}
              isLoading={createLoading}
              isDirty={isDirty}
              submitLabel="Add"
              showCancel={
                !!(quoteDocumentTypes && quoteDocumentTypes.length !== 0)
              }
            />
          )}

          {quoteDocumentTypes?.length && !isAddingNew && !editingId ? (
            <MButton
              variant="tertiary"
              fontSize="sm"
              onClick={() => setIsAddingNew(true)}
              p={0}
              m={0}
              mb={3.5}
            >
              + Add Document Type
            </MButton>
          ) : null}
        </MSettingAccordionItem>
      </Accordion.Root>
    </MStack>
  );
};

export default QuoteDocumentTypes;
